import React from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Typography } from 'antd';
import CountryFlag from 'components/CountryFlag';
import { useTranslation } from 'react-i18next';
import { useI18nFormatters } from 'hooks/i18nFormatters';
import KYBSummaryCompanyName from 'assets/images/company/summary.svg';
import TabTitle from '../../TabTitle';
import KeyFinancialsTable from './KeyFinancialsTable';
import CompanySeparator from '../../Separator';

import './styles.less';

const { Title } = Typography;

const I18N_BASE_PATH = 'pages.private.KYBCompany.pages.companyDetails.subPages.summary';

const Summary = ({ openAddressModal, summaryData: summary }) => {
  const { t: translate } = useTranslation();
  const { i18nFormatDateOnly, i18nFormatCurrency } = useI18nFormatters();

  return (
    <section className="KYB-summary flex-col text-dark">
      <TabTitle
        icon={KYBSummaryCompanyName}
        title={summary.title}
        country={summary.country}
      />

      <Row>
        <Col span={24} className="section-title">
          {translate(
            `${I18N_BASE_PATH}.cards.companyIdentificationInformation.title`,
            'Company identification information'
          )}
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <div className="line-of-information">
            <div className="label-identifier">
              {translate(
                `${I18N_BASE_PATH}.cards.companyIdentificationInformation.identifier`,
                'Identifier'
              )}
            </div>
            <div>{summary.identifier}</div>
          </div>
          <div className="line-of-information">
            <div className="label-identifier">
              {translate(
                `${I18N_BASE_PATH}.cards.companyIdentificationInformation.country`,
                'Country'
              )}
            </div>
            <div>{summary.countryName}</div>
          </div>
          <div className="line-of-information">
            <div className="label-identifier">
              {translate(
                `${I18N_BASE_PATH}.cards.companyIdentificationInformation.taxId`,
                'Tax ID'
              )}
            </div>
            <div>{summary.taxId}</div>
          </div>
          <div className="line-of-information">
            <div className="label-identifier">
              {translate(
                `${I18N_BASE_PATH}.cards.companyIdentificationInformation.reg`,
                'Registration number'
              )}
            </div>
            <div>{summary.number}</div>
          </div>

          <div className="line-of-information">
            <div className="label-identifier">
              {translate(
                `${I18N_BASE_PATH}.cards.companyIdentificationInformation.status`,
                'Status'
              )}
            </div>
            <div>{summary.status}</div>
          </div>
          <div className="line-of-information">
            <div className="label-identifier">
              {translate(
                `${I18N_BASE_PATH}.cards.companyIdentificationInformation.registrationDate`,
                'Registration Date'
              )}
            </div>
            <div>{i18nFormatDateOnly(summary.registrationDate, 1)}</div>
          </div>
        </Col>
      </Row>
      <CompanySeparator className="mrg-top-30" margin={0} />

      <Row>
        <Col sm={24} md={12}>
          <Row>
            <Col span={24} className="section-title">
              {translate(
                `${I18N_BASE_PATH}.cards.companyDetails.title`,
                'Contact details'
              )}
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <div className="line-of-information">
                <div className="label-identifier">
                  {translate(
                    `${I18N_BASE_PATH}.cards.companyDetails.telephone`,
                    'Telephone'
                  )}
                </div>
                <div>{summary.telephone}</div>
              </div>
              <div className="line-of-information">
                <div className="label-identifier">
                  {translate(`${I18N_BASE_PATH}.cards.companyDetails.email`, 'Email')}
                </div>
                <div>{summary.email}</div>
              </div>
              <div className="line-of-information">
                <div className="label-identifier">
                  {translate(`${I18N_BASE_PATH}.cards.companyDetails.url`, 'URL')}
                </div>
                <div>{summary.url}</div>
              </div>
              <div className="line-of-information">
                <div className="label-identifier">
                  {translate(`${I18N_BASE_PATH}.cards.companyDetails.address`, 'Address')}
                </div>
                <div>{summary.address}</div>
              </div>
              {summary.otherAddresses.length > 0 && (
                <a
                  href="/#"
                  rel="noreferrer"
                  className="text-bold company-print-no-render"
                  onClick={(e) => {
                    e.preventDefault();
                    openAddressModal({ addresses: summary.otherAddresses });
                  }}
                >
                  {translate(
                    `${I18N_BASE_PATH}.cards.companyDetails.buttons.otherKnownAddress`,
                    'Other Known Addresses'
                  )}
                </a>
              )}
            </Col>
          </Row>
        </Col>
        <Col sm={24} md={12}>
          <CompanySeparator
            className="display-none company-print-render mrg-top-30"
            margin={0}
          />
          <Row>
            <Col span={24} className="section-title">
              {translate(`${I18N_BASE_PATH}.cards.people.title`, 'People')}
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <div className="line-of-information">
                <div className="label-identifier">
                  {translate(`${I18N_BASE_PATH}.cards.people.directors`, 'Directors')}
                </div>
                <div style={{ textTransform: 'lowercase' }}>
                  <>
                    {summary.directorsLength > 0 ? (
                      <>
                        {' '}
                        {summary.directorsLength}{' '}
                        {translate(
                          `${I18N_BASE_PATH}.cards.people.directors`,
                          'Directors'
                        )}
                      </>
                    ) : (
                      <>-</>
                    )}
                  </>
                </div>
              </div>
              <div className="line-of-information">
                <div className="label-identifier">
                  {translate(`${I18N_BASE_PATH}.cards.people.UBOAndPSC`, 'UBO/PSC')}
                </div>
                <div>{summary.UBOAndPSCLength > 0 ? summary.UBOAndPSCLength : '-'}</div>
              </div>
              <div className="line-of-information">
                <div className="label-identifier">
                  {translate(
                    `${I18N_BASE_PATH}.cards.people.shareholders`,
                    'Shareholders'
                  )}
                </div>
                {summary.shareHoldersLength > 0 ? (
                  <div style={{ textTransform: 'lowercase' }}>
                    {summary.shareHoldersLength}{' '}
                    {translate(
                      `${I18N_BASE_PATH}.cards.people.shareholders`,
                      'Shareholders'
                    )}
                  </div>
                ) : (
                  '-'
                )}
              </div>
              <div className="line-of-information company-print-no-render">
                <div className="label-identifier" />
                <div className="pdd-btm-10" />
              </div>
            </Col>
          </Row>

          <Row className="company-print-no-render">
            <Col span={24}>
              <Link to="?tab=people" className="text-bold">
                {translate(`${I18N_BASE_PATH}.cards.people.links.viewMore`, 'View more')}
              </Link>
            </Col>
          </Row>
        </Col>
      </Row>
      <CompanySeparator className="mrg-top-30" margin={0} />

      <Row>
        <Col span={24} className="section-title">
          {translate(`${I18N_BASE_PATH}.cards.financialRisk.title`, 'Financial risk')}
        </Col>
      </Row>
      <Row>
        <Col sm={24} lg={3}>
          <div className="wrapper-score-and-rating page-break-inside-avoid">
            <div className="score">
              <Title level={5}>{summary.internationalScore}</Title>
              <div className="line" />
              {translate(
                `${I18N_BASE_PATH}.cards.financialRisk.internationalScore`,
                'International Score'
              )}
            </div>
            <div className="score">
              <Title level={5}>
                {i18nFormatCurrency(
                  Number.parseFloat(summary.creditLimitValue) || 0,
                  null,
                  summary.creditLimitCurrency
                )}
              </Title>
              <div className="line" />
              {translate(
                `${I18N_BASE_PATH}.cards.financialRisk.creditLimit`,
                'Credit Limit'
              )}
            </div>
          </div>
        </Col>
        <Col sm={24} lg={21}>
          <KeyFinancialsTable keyFinancialsData={summary.keyFinancials} />
        </Col>
      </Row>
      <CompanySeparator className="mrg-top-30" margin={0} />

      <div className="page-break-inside-avoid">
        <Row>
          <Col span={24} className="section-title">
            {translate(
              `${I18N_BASE_PATH}.cards.ultimateHoldingCompany.title`,
              'Ultimate Holding Company'
            )}
          </Col>
        </Row>
        <Row className="text-bold font-size-12 mrg-btm-20">
          <Col span={9}>
            {translate(
              `${I18N_BASE_PATH}.cards.ultimateHoldingCompany.columns.companyName`,
              'Company Name'
            )}
          </Col>
          <Col span={4}>
            {translate(
              `${I18N_BASE_PATH}.cards.ultimateHoldingCompany.columns.country`,
              'Country'
            )}
          </Col>
          <Col span={5}>
            {translate(
              `${I18N_BASE_PATH}.cards.ultimateHoldingCompany.columns.companyNumber`,
              'Company number'
            )}
          </Col>
          <Col span={6}>
            {translate(
              `${I18N_BASE_PATH}.cards.ultimateHoldingCompany.columns.latestKeyFinancial`,
              'Latest key financial'
            )}
          </Col>
        </Row>
        <Row>
          <Col span={9}>{summary.ultimateParentName}</Col>
          <Col span={4}>
            {summary.ultimateParentCountry ? (
              <CountryFlag country={summary.ultimateParentCountry} />
            ) : (
              '-'
            )}
          </Col>
          <Col span={5}>{summary.ultimateParentNumber}</Col>
          <Col span={6}>
            {i18nFormatDateOnly(summary.ultimateParentLatestKeyFinancial, 1)}
          </Col>
        </Row>
        <CompanySeparator className="mrg-top-30" margin={0} />
      </div>

      <Row>
        <Col span={24} className="section-title">
          {translate(`${I18N_BASE_PATH}.cards.companyLinks.title`, 'Company links')}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <div className="line-of-information">
            <div className="label-identifier">
              {translate(`${I18N_BASE_PATH}.cards.companyLinks.group`, 'Group')}
            </div>
            <div>
              {summary.companyLinksCountCompanies}{' '}
              {translate(
                `${I18N_BASE_PATH}.cards.companyLinks.companiesIn`,
                'companies In'
              )}{' '}
              {summary.companyLinksCountCountries}{' '}
              {translate(`${I18N_BASE_PATH}.cards.companyLinks.countries`, 'countries')}
            </div>
          </div>
          <Row className="mrg-top-30 company-print-no-render">
            <Col span={24}>
              <Link to="?tab=companyLinks" className="text-bold">
                {translate(
                  `${I18N_BASE_PATH}.cards.companyLinks.links.viewMore`,
                  'View more'
                )}
              </Link>
            </Col>
          </Row>
        </Col>
      </Row>
    </section>
  );
};

export default Summary;
